// src/oura/fixtures/nyonya-visibility.ts
//
// Nyonya Table's Visibility, in the payload's own shapes: `results.queries`,
// `results.probes`, `aiVisibility`, `grounding`, answer coverage, Search
// Console totals and the score history. The screen builds its model from this
// through the same function a live business goes through (fromLive.ts), so
// the preview exercises the live path.
//
// One answer per engine per question, as live runs: the ChatGPT app is engine
// `chatgpt_app`, and an `openai` row per question is kept for its links only,
// the way the backend stores an app run. The aggregates (engine rates, named
// count, checks, rivals) are counted from the rows below, so no number here
// can disagree with the table it sits above.
//
// Fields no live payload carries yet are marked where they are set. They are
// here so the preview shows the whole design; on live they are absent and the
// screen shows its fallback.

import type {
    AeoAiVisibility,
    AeoAnswerCoverage,
    AeoBusinessCompetitor,
    AeoCompetitorWin,
    AeoProbe,
    AeoQuery,
    AeoScoreHistoryEntry,
    AeoSourceDomain,
    AeoTrackerSummaryTask,
} from "@service/aeo";
import type { AeoVisibilityCheck, AeoVisibilityCheckName } from "@service/aeo/types";
import type { VisibilityInputs } from "../visibility/fromLive";

const BUSINESS = "Nyonya Table";
const SCAN_AT = "2026-09-16T08:00:00Z";
const CAPTURED_AT = "2026-09-14T09:12:00Z";

interface RivalDef {
    name: string;
    domain: string | null;
    /** Only ever named alongside the business, never in its place. */
    beside?: boolean;
    /** How the rival reads in a ChatGPT list. */
    blurb: string;
    // fixture-only until ourteam-backend handoff: Rival gap sentence
    gap?: string;
    /** The branded report's paragraph on this rival (`report_copy.competitors`). */
    narrative?: string;
}

const RIVALS: Record<string, RivalDef> = {
    B: { name: "Baba's Kitchen", domain: "babaskitchen.my", blurb: "Known for family and group dining, with set menus for 10 or more.", gap: "A family and group dining page that AI quotes for family questions." },
    H: { name: "Heritage Rasa", domain: "heritagerasa.my", blurb: "Near Pavilion KL. Every dish on the menu shows a price.", gap: "A price on every menu page." },
    K: { name: "Kampung Spice", domain: "kampungspice.com", blurb: "Busy and well reviewed, with over 1,200 Google reviews.", gap: "A Google listing with twice the reviews." },
    R: { name: "Rumah Bibik", domain: null, beside: true, blurb: "Home-style nyonya dishes, with a booking page.", gap: "A booking link on every page." },
    // No verified gap: the Competitors detail falls back to the report's paragraph.
    J: {
        name: "Joo Chiat Nyonya", domain: null, beside: true, blurb: "A family kitchen known for its kueh and curries.",
        narrative: "Joo Chiat Nyonya comes up beside you for heritage dishes such as chap chye, usually quoted from food guides rather than its own pages.",
    },
};
const YOU_BLURB = "On Dunearn Road. Nyonya laksa and ayam buah keluak in a restored shophouse.";

type Site = "yes" | "thin" | "none";
/** [question, type, ChatGPT named, Gemini named, Google's AI named, your site, rivals named] */
type Row = [string, AeoVisibilityCheckName, 0 | 1, 0 | 1, 0 | 1, Site, string];

const ROWS: Row[] = [
    ["best Peranakan restaurant Bukit Bintang", "location", 1, 0, 1, "yes", "BH"],
    ["Peranakan restaurant near me Bukit Bintang", "location", 1, 1, 1, "yes", "B"],
    ["authentic laksa near me Bukit Bintang", "location", 1, 1, 1, "yes", "R"],
    ["nyonya food Bukit Bintang", "location", 0, 1, 0, "yes", "BK"],
    ["best peranakan restaurant in Bukit Bintang", "location", 0, 0, 0, "thin", "B"],
    ["Peranakan restaurant Pavilion KL", "location", 1, 0, 0, "thin", "H"],
    ["nyonya restaurant Jalan Alor", "location", 0, 1, 0, "none", "B"],
    ["Peranakan food Holland Village", "location", 0, 0, 0, "none", "H"],
    ["Peranakan restaurant Clementi", "location", 0, 0, 0, "none", "K"],
    ["nyonya laksa Sixth Avenue", "location", 1, 0, 0, "thin", "R"],
    ["Peranakan restaurant near Botanic Gardens", "location", 0, 0, 0, "none", ""],
    ["nyonya kueh Bukit Bintang", "location", 0, 0, 0, "none", "K"],
    ["Peranakan restaurant Dunearn Road", "location", 1, 1, 1, "yes", "R"],
    ["best laksa Bukit Bintang", "location", 1, 1, 1, "yes", "B"],
    ["Peranakan restaurant Lot 10", "location", 0, 0, 0, "none", "H"],
    ["nyonya restaurant King Albert Park", "location", 0, 0, 0, "thin", "BK"],
    ["Peranakan lunch Bukit Bintang", "location", 1, 0, 0, "thin", "R"],
    ["Peranakan dinner Bukit Bintang", "location", 0, 1, 0, "yes", "B"],
    ["ayam buah keluak Bukit Bintang", "location", 0, 0, 0, "thin", "B"],
    ["nyonya food near Bukit Bintang MRT", "location", 0, 0, 0, "none", ""],
    ["Peranakan restaurant Jalan Bukit Bintang", "location", 1, 1, 1, "yes", "H"],
    ["heritage food Bukit Bintang", "location", 0, 0, 0, "none", ""],
    ["Peranakan restaurant Coronation Plaza", "location", 0, 0, 0, "none", ""],
    ["nyonya restaurant Hillview", "location", 0, 0, 0, "none", ""],
    ["Peranakan restaurant Newton", "location", 0, 0, 0, "none", "H"],
    ["best nyonya restaurant Kuala Lumpur", "location", 0, 0, 0, "none", ""],
    ["best ayam buah keluak Kuala Lumpur", "specialty", 0, 0, 0, "yes", "B"],
    ["best nyonya laksa Kuala Lumpur", "specialty", 0, 1, 0, "yes", "B"],
    ["authentic Peranakan cuisine Kuala Lumpur", "specialty", 1, 0, 1, "yes", "R"],
    ["where to eat nyonya food Kuala Lumpur", "specialty", 1, 1, 1, "yes", "BH"],
    ["nyonya kueh platter Kuala Lumpur", "specialty", 0, 1, 0, "none", "K"],
    ["Peranakan catering Kuala Lumpur", "specialty", 0, 0, 0, "none", ""],
    ["nyonya chap chye Kuala Lumpur", "specialty", 0, 1, 0, "thin", "J"],
    ["Peranakan set lunch Kuala Lumpur", "specialty", 0, 0, 0, "none", "H"],
    ["Peranakan restaurant in a heritage shophouse", "specialty", 1, 1, 0, "yes", "R"],
    ["family recipe Peranakan restaurant Kuala Lumpur", "specialty", 1, 0, 0, "thin", "J"],
    ["family friendly Peranakan restaurant", "specialty", 0, 0, 0, "none", "B"],
    ["Peranakan restaurant for tourists Kuala Lumpur", "specialty", 0, 0, 0, "none", "B"],
    ["halal Peranakan restaurant Kuala Lumpur", "specialty", 0, 0, 0, "none", "H"],
    ["Peranakan restaurant for beginners Bukit Bintang", "specialty", 0, 0, 0, "yes", "H"],
    ["how much does Peranakan food cost in Kuala Lumpur", "intent", 0, 0, 0, "none", ""],
    ["Peranakan restaurant Bukit Bintang price", "intent", 0, 0, 0, "none", "H"],
    ["affordable nyonya restaurant Bukit Bintang", "intent", 1, 0, 0, "thin", ""],
    ["Baba's Kitchen vs Nyonya Table", "intent", 1, 1, 1, "yes", "B"],
    ["best Peranakan restaurants Kuala Lumpur ranked", "intent", 0, 0, 0, "none", "B"],
    ["Heritage Rasa alternatives Bukit Bintang", "intent", 0, 0, 0, "none", ""],
    ["which Peranakan restaurant has the best laksa", "intent", 0, 0, 0, "thin", "B"],
    ["date night restaurant Bukit Bintang", "intent", 1, 0, 0, "none", "J"],
    ["restaurant for large group Bukit Bintang", "intent", 1, 0, 0, "none", ""],
    ["book a Peranakan restaurant Bukit Bintang", "intent", 0, 0, 0, "none", ""],
];

/**
 * Slot numbers. The Search Console demo block (devMock) pins its matches to
 * slots 27, 41 and 12, and the Search performance screen reads the same block,
 * so the three questions those searches belong to take those slots here and
 * the two screens agree about which question a search landed on.
 */
const SLOT_SWAP: Record<number, number> = { 1: 27, 27: 1, 3: 41, 41: 3, 30: 12, 12: 30 };
const slotOf = (i: number) => SLOT_SWAP[i + 1] ?? i + 1;

/** Rows where Google showed no AI Overview, and the one it could not read. */
const NO_OVERVIEW = new Set([8, 10, 19, 22, 23, 31, 38, 40]);
const OVERVIEW_FAILED = new Set([44]);

const SOURCE_PAGES = [
    "https://www.tripadvisor.com.my/Restaurants-g294265-Bukit_Timah",
    "https://sethlui.com/best-peranakan-food-singapore/",
    "https://www.hungrygowhere.com/search-results/peranakan/",
];

/* ------------------------------------------------------------------------- *
 * The answers, in the engines' own shape
 * ------------------------------------------------------------------------- */

function rivalCodes(row: Row, named: boolean): string[] {
    // A rival only ever named beside the business is not in an answer that
    // leaves the business out.
    return row[6].split("").filter((c) => named || !RIVALS[c].beside);
}

/** The ChatGPT app's answer: a short list of places, as the app writes it. */
function appList(row: Row): [string, string][] {
    const named = row[2] === 1;
    const list: [string, string][] = rivalCodes(row, named).map((c) => [RIVALS[c].name, RIVALS[c].blurb]);
    if (named) list.splice(row[3] === 1 ? 0 : list.length, 0, [BUSINESS, YOU_BLURB]);
    return list;
}

function appAnswer(row: Row): string {
    const list = appList(row);
    if (list.length > 1) {
        return [
            `Here are ${list.length} places worth a look:`,
            list.map(([n, d], i) => `${i + 1}. **${n}**. ${d}`).join("\n"),
            "Hours and prices change, so check each listing before you go.",
        ].join("\n\n");
    }
    if (list.length === 1) return `The best match is **${list[0][0]}**. ${list[0][1]}\n\nIt is busy at lunch, so book ahead for groups.`;
    return "Few Peranakan restaurants in Bukit Bintang have recent reviews. Most people end up at a national chain or a food court in a mall nearby.";
}

function proseAnswer(row: Row, named: boolean, lead: boolean): string {
    const rivals = rivalCodes(row, named).map((c) => RIVALS[c].name);
    const also = rivals.length ? ` Also worth a look: ${rivals.join(", ")}.` : "";
    if (named && lead) {
        return `For "${row[0]}", ${BUSINESS} on Dunearn Road is the pick: ayam buah keluak cooked from a family recipe, a heritage shophouse, and steady reviews.${also}`;
    }
    if (named) {
        return `${rivals.length ? `${rivals.join(" and ")} ${rivals.length > 1 ? "come" : "comes"} up most often.` : "A few places come up."} ${BUSINESS} is also mentioned, for its laksa and the shophouse setting.`;
    }
    const why = /family|group/.test(row[0]) ? "its family and group dining page"
        : /price|cost|affordable/.test(row[0]) ? "its published prices"
            : "its reviews and listing";
    if (rivals.length > 1) return `The most recommended ${row[1] === "location" ? "spots" : "options"} are ${rivals.join(", ")}. ${rivals[0]} is named first, for ${why}.`;
    if (rivals.length === 1) return `${rivals[0]} is the top pick, for ${why}.`;
    return "The answer lists national chains and a food court in a mall nearby. No Bukit Bintang restaurant is named.";
}

const rivalNames = (row: Row, named: boolean) => rivalCodes(row, named).map((c) => RIVALS[c].name);

function probesFor(row: Row, i: number): AeoProbe[] {
    const [text, , gpt, gem, gai] = row;
    const base = { query: text, query_id: `q${slotOf(i)}`, country: "SG", cited: false, sentiment: null, sentiment_score: null };
    const cites = [SOURCE_PAGES[i % SOURCE_PAGES.length], ...(i % 7 === 0 ? ["https://nyonyatable.com/menu"] : [])];
    const probes: AeoProbe[] = [
        {
            ...base, engine: "chatgpt_app", instrument: "chatgpt_app", engineLabel: "ChatGPT (app)",
            mentioned: gpt === 1, cited_pages: [], answer: appAnswer(row),
            rivals: rivalNames(row, gpt === 1), rivalNames: rivalNames(row, gpt === 1),
        },
        // Kept for its links only when the app ran: never a verdict.
        {
            ...base, engine: "openai", instrument: "openai", engineLabel: "OpenAI (API)",
            mentioned: gpt === 1, cited: true, cited_pages: cites, answer: proseAnswer(row, gpt === 1, false),
            rivals: rivalNames(row, gpt === 1), rivalNames: rivalNames(row, gpt === 1),
        },
        {
            ...base, engine: "gemini", instrument: "api", engineLabel: "Gemini",
            mentioned: gem === 1, cited: true, cited_pages: cites, answer: proseAnswer(row, gem === 1, gpt === 1),
            rivals: rivalNames(row, gem === 1), rivalNames: rivalNames(row, gem === 1),
        },
    ];
    // fixture-only until ourteam-backend handoff: Per-question Google AI Overview readings
    if (!NO_OVERVIEW.has(i) && !OVERVIEW_FAILED.has(i)) {
        probes.push({
            ...base, engine: "google_ai", instrument: "google_serp", engineLabel: "Google AI",
            mentioned: gai === 1, cited_pages: [], answer: proseAnswer(row, gai === 1, true),
            rivals: rivalNames(row, gai === 1), rivalNames: rivalNames(row, gai === 1),
        });
    }
    return probes;
}

/* ------------------------------------------------------------------------- *
 * The capture: a picture of the app's page, drawn as the stored screenshot
 * would show it. Fixture only; live captures come from the answer store.
 * ------------------------------------------------------------------------- */

const xml = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

function wrap(text: string, width: number): string[] {
    const lines: string[] = [];
    let line = "";
    text.split(/\s+/).forEach((word) => {
        if ((line + " " + word).trim().length > width) {
            lines.push(line);
            line = word;
        } else line = `${line} ${word}`.trim();
    });
    if (line) lines.push(line);
    return lines;
}

function captureSrc(row: Row): string {
    const list = appList(row);
    const body: { text: string; bold?: boolean; gap?: number }[] = [];
    if (list.length > 1) {
        body.push({ text: `Here are ${list.length} places worth a look:` });
        list.forEach(([name, blurb], i) => {
            body.push({ text: `${i + 1}. ${name}`, bold: true, gap: 10 });
            wrap(blurb, 74).forEach((l) => body.push({ text: `   ${l}` }));
        });
        body.push({ text: "Hours and prices change, so check each listing before you go.", gap: 12 });
    } else {
        wrap(appAnswer(row).replace(/\*\*/g, ""), 78).forEach((l) => body.push({ text: l }));
    }
    let y = 150;
    const text = body.map((b) => {
        y += (b.gap ?? 0) + 24;
        return `<text x="40" y="${y}" font-size="15" font-weight="${b.bold ? 600 : 400}" fill="#1F2937">${xml(b.text)}</text>`;
    }).join("");
    const h = y + 60;
    const q = xml(row[0]);
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="720" height="${h}" viewBox="0 0 720 ${h}" font-family="Inter, Arial, sans-serif">`
        + `<rect width="720" height="${h}" fill="#fff"/><rect width="720" height="44" fill="#F3F4F6"/>`
        + `<circle cx="20" cy="22" r="5" fill="#D1D5DB"/><circle cx="38" cy="22" r="5" fill="#D1D5DB"/><circle cx="56" cy="22" r="5" fill="#D1D5DB"/>`
        + `<rect x="80" y="11" width="260" height="22" rx="6" fill="#fff"/><text x="92" y="27" font-size="13" fill="#6B7280">chatgpt.com</text>`
        + `<rect x="${Math.max(120, 680 - q.length * 8.2)}" y="72" width="${Math.min(560, q.length * 8.2 + 32)}" height="42" rx="21" fill="#F3F4F6"/>`
        + `<text x="664" y="98" font-size="15" text-anchor="end" fill="#111827">${q}</text>${text}</svg>`;
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/* ------------------------------------------------------------------------- *
 * The payload blocks
 * ------------------------------------------------------------------------- */

const queries: AeoQuery[] = ROWS.map((r, i) => ({
    text: r[0], bucket: r[1], group: r[1], check: r[1], n: slotOf(i), slot_n: slotOf(i), language: "en",
}));

const probes: AeoProbe[] = ROWS.flatMap(probesFor);

const namedGpt = ROWS.filter((r) => r[2] === 1).length;
const namedGem = ROWS.filter((r) => r[3] === 1).length;
const namedAny = ROWS.filter((r) => r[2] === 1 || r[3] === 1).length;
const googleRead = ROWS.filter((_, i) => !NO_OVERVIEW.has(i) && !OVERVIEW_FAILED.has(i));
const namedGai = googleRead.filter((r) => r[4] === 1).length;

// This sample is a local business, so only the four local checks (not the brand ones).
type LocalCheckName = Exclude<AeoVisibilityCheckName, "pick" | "source" | "supplier">;
const CHECK_COPY: Record<LocalCheckName, { question: string; fix: string }> = {
    location: { question: "Can AI find you where you are?", fix: "Listings and a page per area served" },
    specialty: { question: "Does AI know what you actually do?", fix: "Service pages, structured data and clearer positioning" },
    intent: { question: "Does AI surface you at the moment of decision?", fix: "Price pages, comparison pages and booking links" },
    authority: { question: "Does AI treat you as a source worth quoting?", fix: "Guides, FAQs and third-party roundups" },
};

function checkFor(kind: LocalCheckName): AeoVisibilityCheck {
    const rows = ROWS.filter((r) => r[1] === kind);
    const mentioned = rows.reduce((n, r) => n + r[2] + r[3], 0);
    const answered = rows.length * 2;
    return {
        mentioned,
        answered,
        label: `${mentioned} of ${answered} answers`,
        unit: "answers",
        questions: rows.length,
        questionsAnswered: rows.length,
        questionsNamed: rows.filter((r) => r[2] === 1 || r[3] === 1).length,
        questionsExcluded: 0,
        ...CHECK_COPY[kind],
    };
}

/** Which questions each rival was named on, counted from the probes above. */
const competitorWins: AeoCompetitorWin[] = Object.keys(RIVALS).map((code): AeoCompetitorWin => {
    const rival = RIVALS[code];
    const rows = ROWS.filter((r) => rivalCodes(r, r[2] === 1 || r[3] === 1).includes(code));
    const on = rows.map((r) => r[0]);
    // fixture-only until ourteam-backend handoff: Rivals: the questions where a rival was named instead
    const instead = rows.filter((r) => r[2] !== 1 && r[3] !== 1).map((r) => r[0]);
    return {
        name: rival.name,
        queryCount: on.length,
        queries: on,
        insteadCount: instead.length,
        insteadQueries: instead,
        gap: rival.gap ? { text: rival.gap } : null,
        gapAbsent: rival.gap ? null : instead.length === 0 ? "no_instead_questions" : "no_supported_reason",
    };
}).sort((a, b) => b.queryCount - a.queryCount);

/** `businessAudit.competitors` as the backend builds it from the tally above (#4975). */
const auditCompetitors: AeoBusinessCompetitor[] = competitorWins.map((w) => {
    const rival = Object.values(RIVALS).find((r) => r.name === w.name);
    return {
        name: w.name, domain: rival?.domain ?? "", aeo: null, seo: null,
        queries: w.queryCount, note: `Named on ${w.queryCount} of ${ROWS.length} questions`,
    };
});

/** Slots of the named questions, which is where a cited source can come from. */
const namedSlots = ROWS.map((r, i) => (r[2] === 1 || r[3] === 1 ? slotOf(i) : null)).filter((n): n is number => n !== null);

function source(domain: string, count: number, kind: NonNullable<AeoSourceDomain["kind"]>, own = false): AeoSourceDomain {
    return {
        domain, answers: count, own,
        // fixture-only until ourteam-backend handoff: Source rows: questions and kind
        kind, questions: namedSlots.slice(0, count),
    };
}

const aiVisibility: AeoAiVisibility = {
    score: 38,
    scoreAbsent: null,
    verdict: "Low",
    blurb: "Rarely surfaces in AI answers today. High upside once the fixes land.",
    capturedAt: SCAN_AT,
    engineRates: { chatgpt_app: { mentioned: namedGpt, answered: ROWS.length }, gemini: { mentioned: namedGem, answered: ROWS.length } },
    questionsNamed: namedAny,
    mentions: namedGpt + namedGem,
    mentionsDeltaPct: 7,
    citationShare: { count: 3, denominator: ROWS.length, share: 3 / ROWS.length, absent: null, engines: ["gemini", "openai"], basis: "questions_own_domain_cited" },
    citations: 3,
    citationsDeltaPct: null,
    citationsAbsent: null,
    citationsFrom: ["gemini", "openai"],
    citedPages: 21,
    citedPagesDeltaPct: null,
    coverage: {
        headlineExcluded: 0, brandedExcluded: 0, enginesProbed: 2, enginesTotal: 2,
        questionsAsked: ROWS.length, questionsAnswered: ROWS.length, answersScored: ROWS.length * 2,
        recipe: "visibility-set-v4.2", crossChecked: true,
    },
    sourceDomains: [
        source("maps.google.com", 14, "map"),
        source("tripadvisor.com.my", 9, "directory"),
        source("sethlui.com", 6, "media"),
        source("hungrygowhere.com", 5, "directory"),
        source("nyonyatable.com", 3, "own", true),
    ],
    sourceDomainsTotal: 17,
    sourceDomainsBasis: "questions",
    ownDomainAnswers: 3,
    trend: [25, 29, namedGpt + namedGem],
    trendBasis: "per_assessment",
    trendCapturedAt: ["2026-07-07T08:00:00Z", "2026-08-18T08:00:00Z", SCAN_AT],
    engines: [
        { engine: "ChatGPT (app)", instrument: "chatgpt_app", isChatgptApp: true, probed: true, mentions: namedGpt, sharePct: 55, summary: "", rivals: ["Baba's Kitchen", "Heritage Rasa", "Rumah Bibik"] },
        { engine: "OpenAI (API)", instrument: "openai_api", citationOnly: true, probed: true, mentions: 0, sharePct: 0, summary: "", rivals: [] },
        { engine: "Gemini", instrument: "api", probed: true, mentions: namedGem, sharePct: 45, summary: "", rivals: ["Baba's Kitchen", "Kampung Spice", "Joo Chiat Nyonya"] },
        {
            engine: "Google AI", instrument: "google_serp", probed: true, mentions: namedGai, sharePct: Math.round((namedGai / googleRead.length) * 100),
            sharePctBasis: "engine_hit_rate", answered: googleRead.length, summary: "", rivals: ["Baba's Kitchen", "Heritage Rasa"],
            coverage: { asked: ROWS.length, answered: googleRead.length, notShown: NO_OVERVIEW.size, failed: OVERVIEW_FAILED.size },
            capturedAt: SCAN_AT,
        },
    ],
    countries: [],
    checks: { location: checkFor("location"), specialty: checkFor("specialty"), intent: checkFor("intent") },
    competitorWins,
};

const answerCoverage: AeoAnswerCoverage = {
    coveragePct: Math.round((ROWS.filter((r) => r[5] === "yes").length / ROWS.length) * 100),
    answered: ROWS.filter((r) => r[5] === "yes").length,
    total: ROWS.length,
    threshold: 0.62,
    gaps: [],
    matches: ROWS.map((r) => ({
        query: r[0],
        similarity: r[5] === "yes" ? 0.78 : r[5] === "thin" ? 0.55 : 0.31,
        page: r[5] === "none" ? "https://nyonyatable.com/" : "https://nyonyatable.com/menu",
        answered: r[5] === "yes",
        status: r[5] === "yes" ? "covered" : r[5] === "thin" ? "partial" : "missing",
    })),
};

// fixture-only until ourteam-backend handoff: Named-over-time history
const history: AeoScoreHistoryEntry[] = [
    {
        assessment_id: "nyonya-table-latest", captured_at: SCAN_AT, aeo_score: 38, seo_score: 63, gated: false, status: "complete",
        cost: { actual_usd: 0.61, estimated_usd: null },
        visibility: {
            questionsNamed: namedAny, questionsOf: ROWS.length, recipe: "visibility-set-v4.2", queriesFrozenAt: "2026-08-18T08:00:00Z",
            questionSetId: "set-v4.2-nyonya", trendBreak: null,
            engines: {
                chatgpt: { named: namedGpt, answered: ROWS.length, instrument: "chatgpt_app" },
                gemini: { named: namedGem, answered: ROWS.length },
                googleAi: { named: namedGai, answered: googleRead.length, asked: ROWS.length },
            },
        },
    },
    {
        assessment_id: "nyonya-table-2026-08-18", captured_at: "2026-08-18T08:00:00Z", aeo_score: 35, seo_score: 61, gated: false, status: "complete",
        cost: { actual_usd: 0.58, estimated_usd: null },
        visibility: {
            questionsNamed: 21, questionsOf: 50, recipe: "visibility-set-v4.2", queriesFrozenAt: "2026-08-18T08:00:00Z",
            questionSetId: "set-v4.2-nyonya", trendBreak: { at: "2026-08-18T08:00:00Z", reason: "question set re-authored onto visibility-set-v4.2" },
            engines: { chatgpt: { named: 16, answered: 50, instrument: "chatgpt_app" }, gemini: { named: 13, answered: 50 }, googleAi: null },
        },
    },
    {
        assessment_id: "nyonya-table-2026-07-07", captured_at: "2026-07-07T08:00:00Z", aeo_score: 31, seo_score: 58, gated: false, status: "complete",
        cost: { actual_usd: 0.55, estimated_usd: null },
        visibility: {
            questionsNamed: 19, questionsOf: 50, recipe: "visibility-set-v3.1", queriesFrozenAt: "2026-07-06T08:00:00Z",
            questionSetId: "set-v3.1-nyonya", trendBreak: null,
            engines: { chatgpt: { named: 14, answered: 50, instrument: "chatgpt_app" }, gemini: { named: 11, answered: 50 }, googleAi: null },
        },
    },
];

/**
 * The plan tasks minted from a question, as the plan summary route lists
 * them. Ids are the fixture tracker's own, so "View fix" lands on a real row.
 */
const fixTasks: AeoTrackerSummaryTask[] = [
    // The ids and actions are the sample plan's own (tracker.ts builds
    // `${id}-w${week}-t${index}`), so every Fix link opens the task it names.
    { id: "nyonya-table-w2-t1", week: 2, action: "Draft the question and answer entries", ref: "family friendly Peranakan restaurant" },
    { id: "nyonya-table-w2-t0", week: 2, action: "Rewrite the five highest impact pages", ref: "Peranakan restaurant Bukit Bintang price" },
    { id: "nyonya-table-w2-t0", week: 2, action: "Rewrite the five highest impact pages", ref: "best ayam buah keluak Kuala Lumpur" },
    { id: "nyonya-table-w2-t1", week: 2, action: "Draft the question and answer entries", ref: "Peranakan catering Kuala Lumpur" },
    { id: "nyonya-table-w2-t0", week: 2, action: "Rewrite the five highest impact pages", ref: "nyonya restaurant Jalan Alor" },
    { id: "nyonya-table-w2-t1", week: 2, action: "Draft the question and answer entries", ref: "halal Peranakan restaurant Kuala Lumpur" },
].map(({ ref, ...t }) => ({ ...t, status: "not_started", source: { type: "query", ref } }));

/*
 * fixture-only: the 18 Aug scan, which asked these same 50 questions, as the
 * Competitors tab compares against it. A live business fetches that scan and
 * reduces it the same way (`priorScanFrom`). Chosen so every state shows once:
 * a rival up with new questions taken (Baba's Kitchen, 3 new), up by one with
 * one new (Heritage Rasa), unchanged (Kampung Spice), down (Rumah Bibik), and
 * one not named in August at all (Joo Chiat Nyonya).
 */
const PRIOR_ON: Record<string, number> = { B: 15, H: 10, K: 5, R: 7 };
const PRIOR_NEW_WINS: Record<string, string[]> = {
    B: ["best ayam buah keluak Kuala Lumpur", "Peranakan restaurant for tourists Kuala Lumpur", "which Peranakan restaurant has the best laksa"],
    H: ["Peranakan restaurant for beginners Bukit Bintang"],
};
const winsNow = (code: string) =>
    ROWS.filter((r) => r[6].includes(code) && r[2] === 0 && r[3] === 0 && !RIVALS[code].beside).map((r) => r[0]);
const prior = {
    capturedAt: "2026-08-18T08:00:00Z",
    rivals: Object.entries(PRIOR_ON).map(([code, on]) => ({
        name: RIVALS[code].name,
        on,
        winKeys: winsNow(code)
            .filter((q) => !(PRIOR_NEW_WINS[code] ?? []).includes(q))
            .map((q) => q.trim().toLowerCase()),
    })),
};

/** Everything but the two Search Console blocks, which come from devMock so
 *  this screen and Search performance read the same ones. */
export const nyonyaVisibility: Omit<VisibilityInputs, "searchQuestions" | "searchPerformance"> = {
    capturedAt: SCAN_AT,
    assessmentId: "nyonya-table-latest",
    vis: aiVisibility,
    queries,
    probes,
    // No Google readings: this fixture is the scan-without-them case, which is
    // exactly the state older scans are in. The preview therefore exercises the
    // fallback — Google as one total, not per question — rather than hiding it.
    googleAiReadings: undefined,
    grounding: {
        instrument: "chatgpt_app",
        instrumentLabel: "ChatGPT (app)",
        instrumentNote: null,
        mixed: false,
        askedQuestions: ROWS.length,
        totalQuestions: ROWS.length,
        costUsd: 0.61,
        citationsMeasurable: false,
    },
    answerCoverage,
    trendBreak: { at: "2026-08-18T08:00:00Z", reason: "question set re-authored onto visibility-set-v4.2" },
    auditCompetitors,
    reportCompetitors: Object.values(RIVALS)
        .filter((r): r is RivalDef & { narrative: string } => !!r.narrative)
        .map((r) => ({ name: r.name, narrative: r.narrative })),
    history,
    levelCappedBy: "rented_visibility",
    // fixture-only until ourteam-backend handoff: Benchmark for the merchant sentence
    benchmark: 10,
    // Drawn only for the question a reader opens, not for all fifty up front.
    captureFor: (slot) => {
        const i = ROWS.findIndex((_, k) => slotOf(k) === slot);
        return i < 0 ? null : { src: captureSrc(ROWS[i]), takenAt: CAPTURED_AT };
    },
    fixTasks,
    prior,
};
